import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import JourneyCalendar from "@/components/JourneyCalendar";
import { EASE, FadeUp } from "@/components/Reveal";
import { ArrowLeft, Video, MapPin, Loader2, MessageCircle, LogIn, UserPlus, User } from "lucide-react";
import { waLink, buildBookingWhatsAppMessage } from "@/data/site";

const API = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${API}/api`, withCredentials: true });

const STEPS = [
  { id: "identificacao", label: "Você" },
  { id: "servico", label: "Atendimento" },
  { id: "modalidade", label: "Modalidade" },
  { id: "data", label: "Data" },
  { id: "horario", label: "Horário" },
  { id: "resumo", label: "Revisão" },
];

const MODALITY_META = {
  online: { label: "Online", desc: "Por videochamada, de onde você estiver", icon: Video },
  presencial: { label: "Presencial", desc: "Metropolitan Office, Taboão da Serra — SP", icon: MapPin },
};

export const formatDateLong = (iso) =>
  iso
    ? new Date(`${iso}T12:00:00`).toLocaleDateString("pt-BR", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";

const stepTransition = {
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -14 },
  transition: { duration: 0.45, ease: EASE },
};

const OptionCard = ({ selected, onClick, icon: Icon, title, desc, testId }) => (
  <button
    type="button"
    data-testid={testId}
    aria-pressed={selected}
    onClick={onClick}
    className={`group flex w-full items-start gap-5 rounded-3xl border p-6 text-left transition-[transform,border-color,background-color] duration-300 hover:-translate-y-0.5 sm:p-7 ${
      selected
        ? "border-gold-500/70 bg-gold-100/40"
        : "border-raiz-900/10 bg-cream hover:border-raiz-600/40"
    }`}
  >
    {Icon && (
      <span
        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full border transition-colors duration-300 ${
          selected ? "border-gold-500/60 text-gold-700" : "border-raiz-900/10 text-raiz-700"
        }`}
      >
        <Icon aria-hidden="true" className="h-5 w-5" />
      </span>
    )}
    <span>
      <span className="block font-serif text-xl text-ink">{title}</span>
      {desc && <span className="mt-1.5 block text-sm leading-relaxed text-ink/60">{desc}</span>}
    </span>
  </button>
);

const SummaryRow = ({ label, value, testId }) => (
  <div className="flex items-start justify-between gap-6 border-b border-raiz-900/8 py-3.5 last:border-0">
    <span className="text-xs uppercase tracking-[0.18em] text-ink/45">{label}</span>
    <span className={`text-right text-sm ${value ? "font-medium text-ink" : "text-ink/35"}`} data-testid={testId}>
      {value || "—"}
    </span>
  </div>
);

const inputCls =
  "w-full rounded-2xl border border-raiz-800/20 bg-paper px-5 py-3.5 text-sm text-ink placeholder:text-ink/35 transition-colors duration-300 focus:border-raiz-700 focus:outline-none";

const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
const nameOk = (v) => v.trim().split(/\s+/).filter(Boolean).length >= 2;
const whatsOk = (v) => v.replace(/\D/g, "").length >= 10;

const Agendar = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [services, setServices] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [step, setStep] = useState(0);
  const [serviceSlug, setServiceSlug] = useState(slug || null);
  const [modality, setModality] = useState(null);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [form, setForm] = useState({ name: "", whatsapp: "", email: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [hold, setHold] = useState(null);

  const [me, setMe] = useState(null);
  const [authMode, setAuthMode] = useState(null); // null | "login" | "cadastro" | "convidado"
  const [authForm, setAuthForm] = useState({ name: "", email: "", whatsapp: "", password: "" });
  const [authLoading, setAuthLoading] = useState(false);

  const service = services?.find((s) => s.slug === serviceSlug) || null;

  useEffect(() => {
    api.get("/auth/me").then((res) => {
      setMe(res.data);
      setForm((f) => ({ ...f, name: res.data.name || f.name, email: res.data.email || f.email, whatsapp: res.data.whatsapp || f.whatsapp }));
    }).catch(() => {});
  }, []);

  const releaseHold = async () => {
    const current = hold;
    setHold(null);
    if (current) {
      try {
        await api.delete(`/journey/holds/${current.hold_id}`, { params: { token: current.hold_token } });
      } catch {
        /* reserva expira sozinha */
      }
    }
  };

  const chooseService = async (s) => {
    await releaseHold();
    setServiceSlug(s.slug);
    setModality(s.modalities?.length === 1 ? s.modalities[0] : null);
    setDate(null);
    setTime(null);
  };

  const chooseDate = async (d) => {
    await releaseHold();
    setTime(null);
    setDate(d);
  };

  const chooseTime = async (t) => {
    if (time === t) return;
    await releaseHold();
    setTime(null);
    try {
      const res = await api.post("/journey/holds", { service_slug: serviceSlug, date, time: t });
      setHold({ hold_id: res.data.hold_id, hold_token: res.data.hold_token });
      setTime(t);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Este horário acabou de ser reservado por outra pessoa.");
      const res = await api.get("/journey/availability", { params: { date } }).catch(() => null);
      if (res) setAvailability(res.data.times);
    }
  };

  useEffect(() => {
    setLoadError(false);
    Promise.all([api.get("/journey/services"), api.get("/journey/schedule")])
      .then(([svc, sch]) => {
        const list = svc.data || [];
        setServices(list);
        setSchedule(sch.data);
        if (slug) {
          const found = list.find((s) => s.slug === slug);
          if (found) {
            setServiceSlug(found.slug);
            if (found.modalities?.length === 1) setModality(found.modalities[0]);
          } else {
            setServiceSlug(null);
          }
        }
      })
      .catch(() => setLoadError(true));
  }, [slug]);

  const holdRef = useRef(null);
  holdRef.current = hold;
  useEffect(() => {
    return () => {
      const current = holdRef.current;
      if (current) {
        api.delete(`/journey/holds/${current.hold_id}`, { params: { token: current.hold_token } }).catch(() => {});
      }
    };
  }, []);

  useEffect(() => {
    if (!date) return;
    setTime(null);
    setAvailability(null);
    api
      .get("/journey/availability", { params: { date } })
      .then((res) => setAvailability(res.data.times))
      .catch(() => setAvailability([]));
  }, [date]);

  const doLogin = async () => {
    if (!emailOk(authForm.email)) return toast.error("Informe um e-mail válido.");
    if (!authForm.password) return toast.error("Informe sua senha.");
    setAuthLoading(true);
    try {
      const res = await api.post("/auth/login", { email: authForm.email.trim(), password: authForm.password });
      setMe(res.data);
      setForm((f) => ({ ...f, name: res.data.name || f.name, email: res.data.email || f.email, whatsapp: res.data.whatsapp || f.whatsapp }));
      toast.success(`Olá, ${res.data.name.split(/\s+/)[0]}! Seus dados já foram preenchidos.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível entrar agora.");
    } finally {
      setAuthLoading(false);
    }
  };

  const doRegister = async () => {
    if (!nameOk(authForm.name)) return toast.error("Informe seu nome completo.");
    if (!emailOk(authForm.email)) return toast.error("Informe um e-mail válido.");
    if (!whatsOk(authForm.whatsapp)) return toast.error("Informe um WhatsApp válido com DDD.");
    if (authForm.password.length < 6) return toast.error("Crie uma senha com pelo menos 6 caracteres.");
    setAuthLoading(true);
    try {
      const res = await api.post("/auth/register", {
        name: authForm.name.trim(),
        email: authForm.email.trim(),
        whatsapp: authForm.whatsapp.trim(),
        password: authForm.password,
      });
      setMe(res.data);
      setForm((f) => ({ ...f, name: res.data.name, email: res.data.email, whatsapp: authForm.whatsapp.trim() }));
      toast.success("Cadastro criado! Seus dados já foram preenchidos.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível criar o cadastro agora.");
    } finally {
      setAuthLoading(false);
    }
  };

  const doLogout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setMe(null);
    setAuthMode(null);
    setAuthForm({ name: "", email: "", whatsapp: "", password: "" });
  };

  const validateStep = () => {
    if (step === 0 && !me) {
      if (authMode === "convidado") {
        if (!nameOk(form.name)) return "Informe seu nome completo.";
        if (!whatsOk(form.whatsapp)) return "Informe um WhatsApp válido com DDD.";
        if (!emailOk(form.email)) return "Informe um e-mail válido.";
        return null;
      }
      return "Entre, crie seu cadastro ou escolha continuar sem cadastro.";
    }
    if (step === 1 && !serviceSlug) return "Escolha o atendimento para continuar.";
    if (step === 2 && !modality) return "Escolha a modalidade para continuar.";
    if (step === 3 && !date) return "Escolha uma data disponível.";
    if (step === 4 && !time) return "Escolha um horário disponível.";
    return null;
  };

  const next = async () => {
    const error = validateStep();
    if (error) {
      toast.error(error);
      return;
    }
    if (step < STEPS.length - 1) {
      setStep((s) => s + 1);
      return;
    }
    setSubmitting(true);
    const waWindow = window.open("", "_blank", "noopener,noreferrer");
    try {
      const res = await api.post("/journey/bookings", {
        service_slug: serviceSlug,
        modality,
        date,
        time,
        name: form.name.trim(),
        whatsapp: form.whatsapp.trim(),
        email: form.email.trim(),
        notes: form.notes.trim(),
        payment_preference: "whatsapp",
        hold_id: hold?.hold_id || null,
      });
      const waUrl = waLink(buildBookingWhatsAppMessage(res.data));
      if (waWindow) waWindow.location = waUrl;
      else window.location.href = waUrl;
      navigate(`/confirmacao/${res.data.protocol}`);
    } catch (err) {
      if (waWindow) waWindow.close();
      const detail = err.response?.data?.detail;
      toast.error(detail || "Não foi possível concluir agora. Tente novamente.");
      if (err.response?.status === 409) {
        await releaseHold();
        setStep(4);
        setTime(null);
        api.get("/journey/availability", { params: { date } }).then((res) => setAvailability(res.data.times));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <section className="container-x flex min-h-[70vh] flex-col items-start justify-center pt-28" data-testid="agendar-not-found">
        <p className="eyebrow">Agendamento</p>
        <h1 className="mt-4 font-serif text-4xl font-light text-ink sm:text-5xl">Não foi possível carregar</h1>
        <Link to="/" data-testid="agendar-not-found-home" className="mt-8 text-sm text-raiz-800 underline underline-offset-4">
          Voltar ao início
        </Link>
      </section>
    );
  }

  const authTabCls = (active) =>
    `flex-1 rounded-full border px-4 py-2.5 text-xs font-medium tracking-wide transition-colors duration-300 sm:text-sm ${
      active ? "border-raiz-800 bg-raiz-800 text-paper" : "border-raiz-900/15 text-ink/60 hover:border-raiz-600/50 hover:text-raiz-800"
    }`;

  return (
    <>
      <SEO
        title="Agendar atendimento | Sabrina Mota"
        description="Escolha o atendimento, a modalidade, a data e o horário. A confirmação acontece pelo WhatsApp."
        path={slug ? `/agendar/${slug}` : "/agendar"}
      />
      <section className="pt-28 lg:pt-36" data-testid="agendar-page">
        <div className="container-x pb-24">
          <FadeUp>
            <p className="eyebrow" data-testid="agendar-eyebrow">Agendamento</p>
            <h1 className="mt-5 font-serif text-3xl font-light leading-[1.1] tracking-tight text-ink sm:text-4xl lg:text-5xl" data-testid="agendar-title">
              Agende seu atendimento
            </h1>
          </FadeUp>

          {!services ? (
            <div className="mt-12 h-64 animate-pulse rounded-3xl border border-raiz-900/10 bg-cream" data-testid="agendar-loading" />
          ) : (
            <div className="mt-10 grid items-start gap-10 lg:mt-14 lg:grid-cols-12">
              <aside className="order-first lg:order-last lg:col-span-5" data-testid="agendar-summary">
                <div className="rounded-3xl border border-raiz-900/10 bg-cream p-7 lg:sticky lg:top-28">
                  <p className="text-xs uppercase tracking-[0.24em] text-raiz-600">Seu atendimento</p>
                  <div className="mt-4">
                    <SummaryRow label="Nome" value={form.name.trim() || null} testId="summary-nome" />
                    <SummaryRow label="Atendimento" value={service?.title || null} testId="summary-servico" />
                    <SummaryRow label="Modalidade" value={modality ? MODALITY_META[modality].label : null} testId="summary-modalidade" />
                    <SummaryRow label="Data" value={date ? formatDateLong(date) : null} testId="summary-data" />
                    <SummaryRow label="Horário" value={time} testId="summary-horario" />
                  </div>
                  <p className="mt-5 text-xs leading-relaxed text-ink/50" data-testid="summary-note">
                    Sem cobrança pelo site. Valor e orientações serão informados pela Sabrina pelo WhatsApp.
                  </p>
                </div>
              </aside>

              <div className="lg:col-span-7">
                <ol className="flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="agendar-progress">
                  {STEPS.map((s, i) => (
                    <li key={s.id} className="flex items-center gap-2">
                      <span
                        className={`flex h-7 w-7 items-center justify-center rounded-full border text-[11px] font-medium transition-colors duration-300 ${
                          i < step
                            ? "border-raiz-800 bg-raiz-800 text-paper"
                            : i === step
                              ? "border-gold-500 text-gold-700"
                              : "border-raiz-900/15 text-ink/40"
                        }`}
                        data-testid={`progress-step-${s.id}`}
                      >
                        {i + 1}
                      </span>
                      <span className={`hidden text-xs sm:inline ${i === step ? "text-ink" : "text-ink/40"}`}>{s.label}</span>
                    </li>
                  ))}
                </ol>

                <div className="mt-10">
                  <AnimatePresence mode="wait">
                    <motion.div key={step} {...stepTransition}>
                      {step === 0 && (
                        <div className="space-y-6" data-testid="step-identificacao">
                          <div>
                            <h2 className="font-serif text-2xl font-light text-ink">Antes de começar, quem é você?</h2>
                            <p className="mt-2 text-sm leading-relaxed text-ink/60">
                              Entre ou crie seu cadastro para agilizar — ou continue apenas com seus dados de contato. Nada de formulários longos ou informações clínicas.
                            </p>
                          </div>

                          {me ? (
                            <div className="rounded-3xl border border-gold-400/50 bg-gold-100/40 p-6 sm:p-7" data-testid="ident-logged-card">
                              <p className="text-xs uppercase tracking-[0.2em] text-raiz-600">Você está conectado(a)</p>
                              <p className="mt-3 font-serif text-xl text-ink" data-testid="ident-logged-name">{me.name}</p>
                              <div className="mt-4 space-y-1 text-sm text-ink/65">
                                <p data-testid="ident-logged-email">{me.email}</p>
                                {me.whatsapp && <p data-testid="ident-logged-whats">{me.whatsapp}</p>}
                              </div>
                              <button
                                type="button"
                                data-testid="ident-logout-button"
                                onClick={doLogout}
                                className="mt-5 text-xs text-raiz-800 underline underline-offset-4 transition-colors duration-300 hover:text-raiz-600"
                              >
                                Não é você? Sair e usar outros dados
                              </button>
                            </div>
                          ) : (
                            <>
                              <div className="flex gap-2" data-testid="ident-tabs">
                                <button type="button" data-testid="ident-tab-login" onClick={() => setAuthMode("login")} className={authTabCls(authMode === "login")}>
                                  Entrar
                                </button>
                                <button type="button" data-testid="ident-tab-cadastro" onClick={() => setAuthMode("cadastro")} className={authTabCls(authMode === "cadastro")}>
                                  Criar cadastro
                                </button>
                                <button type="button" data-testid="ident-tab-convidado" onClick={() => setAuthMode("convidado")} className={authTabCls(authMode === "convidado")}>
                                  Sem cadastro
                                </button>
                              </div>

                              {authMode === "login" && (
                                <div className="space-y-4 rounded-3xl border border-raiz-900/10 bg-cream p-6 sm:p-7" data-testid="ident-login-form">
                                  <div>
                                    <label htmlFor="ag-login-email" className="mb-2 block text-sm font-medium text-ink">E-mail</label>
                                    <input id="ag-login-email" type="email" data-testid="login-email-input" className={inputCls} value={authForm.email} onChange={(e) => setAuthForm((f) => ({ ...f, email: e.target.value }))} placeholder="voce@email.com" autoComplete="email" />
                                  </div>
                                  <div>
                                    <label htmlFor="ag-login-senha" className="mb-2 block text-sm font-medium text-ink">Senha</label>
                                    <input id="ag-login-senha" type="password" data-testid="login-password-input" className={inputCls} value={authForm.password} onChange={(e) => setAuthForm((f) => ({ ...f, password: e.target.value }))} placeholder="Sua senha" autoComplete="current-password" />
                                  </div>
                                  <button
                                    type="button"
                                    data-testid="login-submit-button"
                                    onClick={doLogin}
                                    disabled={authLoading}
                                    className="inline-flex items-center gap-2.5 rounded-full bg-raiz-800 px-6 py-3 text-sm font-medium text-paper transition-colors duration-300 hover:bg-raiz-700 disabled:opacity-60"
                                  >
                                    {authLoading ? "Entrando…" : "Entrar"}
                                    {authLoading ? <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" /> : <LogIn aria-hidden="true" className="h-4 w-4" />}
                                  </button>
                                </div>
                              )}

                              {authMode === "cadastro" && (
                                <div className="space-y-4 rounded-3xl border border-raiz-900/10 bg-cream p-6 sm:p-7" data-testid="ident-cadastro-form">
                                  <div>
                                    <label htmlFor="ag-cad-nome" className="mb-2 block text-sm font-medium text-ink">Nome completo</label>
                                    <input id="ag-cad-nome" data-testid="cadastro-nome-input" className={inputCls} value={authForm.name} onChange={(e) => setAuthForm((f) => ({ ...f, name: e.target.value }))} placeholder="Seu nome completo" autoComplete="name" />
                                  </div>
                                  <div>
                                    <label htmlFor="ag-cad-email" className="mb-2 block text-sm font-medium text-ink">E-mail</label>
                                    <input id="ag-cad-email" type="email" data-testid="cadastro-email-input" className={inputCls} value={authForm.email} onChange={(e) => setAuthForm((f) => ({ ...f, email: e.target.value }))} placeholder="voce@email.com" autoComplete="email" />
                                  </div>
                                  <div>
                                    <label htmlFor="ag-cad-whats" className="mb-2 block text-sm font-medium text-ink">WhatsApp</label>
                                    <input id="ag-cad-whats" data-testid="cadastro-whats-input" className={inputCls} value={authForm.whatsapp} onChange={(e) => setAuthForm((f) => ({ ...f, whatsapp: e.target.value }))} placeholder="(11) 90000-0000" inputMode="tel" autoComplete="tel" />
                                  </div>
                                  <div>
                                    <label htmlFor="ag-cad-senha" className="mb-2 block text-sm font-medium text-ink">Crie uma senha</label>
                                    <input id="ag-cad-senha" type="password" data-testid="cadastro-password-input" className={inputCls} value={authForm.password} onChange={(e) => setAuthForm((f) => ({ ...f, password: e.target.value }))} placeholder="Mínimo de 6 caracteres" autoComplete="new-password" />
                                  </div>
                                  <button
                                    type="button"
                                    data-testid="cadastro-submit-button"
                                    onClick={doRegister}
                                    disabled={authLoading}
                                    className="inline-flex items-center gap-2.5 rounded-full bg-raiz-800 px-6 py-3 text-sm font-medium text-paper transition-colors duration-300 hover:bg-raiz-700 disabled:opacity-60"
                                  >
                                    {authLoading ? "Criando…" : "Criar meu cadastro"}
                                    {authLoading ? <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" /> : <UserPlus aria-hidden="true" className="h-4 w-4" />}
                                  </button>
                                </div>
                              )}

                              {authMode === "convidado" && (
                                <div className="space-y-4 rounded-3xl border border-raiz-900/10 bg-cream p-6 sm:p-7" data-testid="ident-convidado-form">
                                  <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-raiz-600">
                                    <User aria-hidden="true" className="h-3.5 w-3.5" /> Só o essencial
                                  </p>
                                  <div>
                                    <label htmlFor="ag-nome" className="mb-2 block text-sm font-medium text-ink">Nome completo</label>
                                    <input id="ag-nome" data-testid="dados-nome-input" className={inputCls} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Seu nome completo" autoComplete="name" />
                                  </div>
                                  <div>
                                    <label htmlFor="ag-whats" className="mb-2 block text-sm font-medium text-ink">WhatsApp</label>
                                    <input id="ag-whats" data-testid="dados-whats-input" className={inputCls} value={form.whatsapp} onChange={(e) => setForm((f) => ({ ...f, whatsapp: e.target.value }))} placeholder="(11) 90000-0000" inputMode="tel" autoComplete="tel" />
                                  </div>
                                  <div>
                                    <label htmlFor="ag-email" className="mb-2 block text-sm font-medium text-ink">E-mail</label>
                                    <input id="ag-email" type="email" data-testid="dados-email-input" className={inputCls} value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="voce@email.com" autoComplete="email" />
                                  </div>
                                </div>
                              )}

                              {(me || authMode) && (
                                <div>
                                  <label htmlFor="ag-notes" className="mb-2 block text-sm font-medium text-ink">
                                    Algo a acrescentar? <span className="font-normal text-ink/45">(opcional)</span>
                                  </label>
                                  <textarea
                                    id="ag-notes"
                                    data-testid="dados-notes-input"
                                    rows={3}
                                    maxLength={600}
                                    className={`${inputCls} resize-none`}
                                    value={form.notes}
                                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                                    placeholder={'Ex.: "Tenho preferência por atendimento à tarde", "É minha primeira consulta", "Gostaria de tirar uma dúvida antes".'}
                                  />
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}

                      {step === 1 && (
                        <div className="space-y-4" data-testid="step-servico">
                          <h2 className="font-serif text-2xl font-light text-ink">Qual atendimento você procura?</h2>
                          {services.map((s) => (
                            <OptionCard
                              key={s.slug}
                              testId={`servico-${s.slug}`}
                              selected={serviceSlug === s.slug}
                              onClick={() => chooseService(s)}
                              title={s.title}
                              desc={s.description}
                            />
                          ))}
                        </div>
                      )}

                      {step === 2 && service && (
                        <div className="space-y-4" data-testid="step-modalidade">
                          <h2 className="font-serif text-2xl font-light text-ink">Como você prefere ser atendido(a)?</h2>
                          <div className="grid gap-4 sm:grid-cols-2">
                            {service.modalities.map((m) => (
                              <OptionCard
                                key={m}
                                testId={`modalidade-${m}`}
                                selected={modality === m}
                                onClick={() => setModality(m)}
                                icon={MODALITY_META[m].icon}
                                title={MODALITY_META[m].label}
                                desc={MODALITY_META[m].desc}
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {step === 3 && (
                        <div data-testid="step-data">
                          <h2 className="font-serif text-2xl font-light text-ink">Qual dia funciona para você?</h2>
                          <div className="mt-6">
                            <JourneyCalendar schedule={schedule} selected={date} onSelect={chooseDate} />
                          </div>
                        </div>
                      )}

                      {step === 4 && (
                        <div data-testid="step-horario">
                          <h2 className="font-serif text-2xl font-light text-ink">Horários para {formatDateLong(date)}</h2>
                          <div className="mt-6">
                            {!availability ? (
                              <div className="grid grid-cols-3 gap-3 sm:grid-cols-4" data-testid="horario-loading">
                                {[...Array(8)].map((_, i) => (
                                  <div key={i} className="h-12 animate-pulse rounded-full bg-cream" />
                                ))}
                              </div>
                            ) : availability.length === 0 ? (
                              <p className="text-sm text-ink/60" data-testid="horario-vazio">
                                Este dia não tem horários disponíveis. Volte e escolha outra data, por favor.
                              </p>
                            ) : (
                              <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                                {availability.map((t) => (
                                  <button
                                    key={t.time}
                                    type="button"
                                    data-testid={`horario-${t.time.replace(":", "")}`}
                                    data-state={t.state}
                                    disabled={!t.available}
                                    onClick={() => chooseTime(t.time)}
                                    className={`rounded-full border py-3 text-sm transition-colors duration-300 ${
                                      time === t.time
                                        ? "border-raiz-800 bg-raiz-800 font-medium text-paper"
                                        : t.available
                                          ? "border-raiz-800/20 text-raiz-900 hover:border-gold-500 hover:text-gold-700"
                                          : t.state === "reservado"
                                            ? "cursor-not-allowed border-gold-500/40 bg-gold-100/40 text-gold-700/70"
                                            : "cursor-not-allowed border-raiz-900/5 text-ink/25 line-through"
                                    }`}
                                  >
                                    {t.time}
                                  </button>
                                ))}
                              </div>
                            )}
                            <div className="mt-4 flex flex-wrap gap-4 text-xs text-ink/55" data-testid="horario-legenda">
                              <span className="inline-flex items-center gap-1.5">
                                <span className="h-2 w-2 rounded-full bg-raiz-600" /> Disponível
                              </span>
                              <span className="inline-flex items-center gap-1.5">
                                <span className="h-2 w-2 rounded-full bg-gold-500" /> Reservado
                              </span>
                              <span className="inline-flex items-center gap-1.5">
                                <span className="h-2 w-2 rounded-full bg-neutral-300" /> Indisponível
                              </span>
                            </div>
                            {time && (
                              <p className="mt-5 rounded-2xl border border-gold-400/40 bg-gold-100/40 p-4 text-sm text-ink/75" data-testid="horario-reserva-note">
                                Horário reservado para você por 20 minutos — tempo de concluir com calma.
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {step === 5 && (
                        <div className="space-y-5" data-testid="step-resumo">
                          <h2 className="font-serif text-2xl font-light text-ink">Confira seus dados</h2>
                          <div className="rounded-3xl border border-raiz-900/10 bg-cream p-6 sm:p-7" data-testid="resumo-final">
                            <SummaryRow label="Nome" value={form.name.trim()} testId="resumo-nome" />
                            <SummaryRow label="WhatsApp" value={form.whatsapp.trim()} testId="resumo-whats" />
                            <SummaryRow label="E-mail" value={form.email.trim()} testId="resumo-email" />
                            <SummaryRow label="Atendimento" value={service?.title} testId="resumo-servico" />
                            <SummaryRow label="Modalidade" value={MODALITY_META[modality]?.label} testId="resumo-modalidade" />
                            <SummaryRow label="Data" value={formatDateLong(date)} testId="resumo-data" />
                            <SummaryRow label="Horário" value={time} testId="resumo-horario" />
                          </div>
                          {form.notes.trim() && (
                            <p className="rounded-2xl border border-raiz-900/10 bg-paper p-4 text-sm leading-relaxed text-ink/65" data-testid="resumo-notes">
                              <span className="font-medium text-ink">Sua observação: </span>
                              {form.notes.trim()}
                            </p>
                          )}
                          <p className="text-sm leading-relaxed text-ink/60" data-testid="resumo-info">
                            Ao continuar, o WhatsApp abre com a mensagem pronta — você só precisa enviar. Valor e orientações serão informados pela Sabrina pelo WhatsApp. Escolher um horário aqui <strong>não</strong> confirma o atendimento sozinho.
                          </p>
                        </div>
                      )}
                    </motion.div>
                  </AnimatePresence>

                  <div className="mt-10 flex items-center justify-between gap-4">
                    <button
                      type="button"
                      data-testid="agendar-voltar-button"
                      onClick={() => setStep((s) => Math.max(0, s - 1))}
                      disabled={step === 0 || submitting}
                      className="inline-flex items-center gap-2 text-sm text-raiz-800 transition-colors duration-300 hover:text-raiz-600 disabled:invisible"
                    >
                      <ArrowLeft aria-hidden="true" className="h-4 w-4" />
                      Voltar
                    </button>
                    <button
                      type="button"
                      data-testid="agendar-continuar-button"
                      onClick={next}
                      disabled={submitting}
                      className="group inline-flex items-center justify-center gap-3 rounded-full bg-raiz-800 px-7 py-3.5 text-sm font-medium tracking-wide text-paper transition-colors duration-300 hover:bg-raiz-700 disabled:opacity-60"
                    >
                      {submitting ? (
                        <>
                          Registrando…
                          <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
                        </>
                      ) : step === STEPS.length - 1 ? (
                        <>
                          Continuar pelo WhatsApp
                          <MessageCircle aria-hidden="true" className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </>
                      ) : (
                        <>
                          Continuar
                          <ArrowLeft aria-hidden="true" className="h-4 w-4 rotate-180 transition-transform duration-300 group-hover:translate-x-1.5" />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Agendar;
